import { depthFirst, interpolate } from "@lernetz/svelte-common/functions";

export function mapRequests<G = { [key: string]: Request }>(data): G {
  return depthFirst(data, (item) => {
    // decodeURL from laravel routes that can not send route params with curly braces
    if (item && item.url) return new RequestBuilder({ ...item, url: decodeURI(item.url) });
    return item;
  });
}

interface RequestBuilderConfig extends Partial<RequestInit> {
  url: string;
}

const FAKE_HOST = "https://fa.ke";

export class RequestBuilder {
  private config: RequestBuilderConfig;

  constructor(config: RequestBuilderConfig | string) {
    this.config = typeof config == "string" ? { url: config } : config;
  }

  vars(vars: { [key: string]: any } = {}): RequestBuilder {
    return new RequestBuilder({ ...this.config, url: interpolate(this.config.url, vars) });
  }

  headers(headers: Record<string, string>) {
    return new RequestBuilder({
      ...this.config,
      headers: Object.assign(this.config.headers || {}, headers),
    });
  }

  method(method: string) {
    return new RequestBuilder({ ...this.config, method });
  }

  body(body: BodyInit | null) {
    return new RequestBuilder({ ...this.config, body });
  }

  query(params: URLSearchParams) {
    let url = this.getURL();
    for (const [key, value] of params) {
      url.searchParams.set(key, value);
    }

    return new RequestBuilder({ ...this.config, url: decodeURI(url.href) });
  }

  hash(hash: string) {
    let url = this.getURL();
    url.hash = hash;
    return new RequestBuilder({ ...this.config, url: decodeURI(url.href) });
  }

  relative(path: string) {
    let u = this.getURL();
    u.pathname = [...u.pathname.split("/"), ...path.split("/")].join("/");

    return new RequestBuilder({ ...this.config, url: decodeURI(u.href) });
  }

  jsonApi(relative: string, data: {} = {}, headers: {} = {}) {
    let route = this
        .relative(relative)
        .body(JSON.stringify(data))
        .headers({...{ "Accept": "application/json", "Content-Type": "application/json" }, ...headers})
        .method("POST");

    return route
        .fetch()
        .then((response) => response.json()) // turn into json
        .then((json) => json?.data); // retrive json api data
}

  fetch() {
    return fetch(new Request(this.url, this.config)).then((response) => {
      if (!response.ok) throw new Error("network response was not OK: " + response.statusText);
      return response;
    });
  }

  get url() {
    return this.getURL().href.replace(FAKE_HOST, "");
  }

  private getURL() {
    let u = new URL(this.config.url, FAKE_HOST);
    u.pathname = u.pathname.replace(/\/+/g, "/");
    return u;
  }
}
